import React, { useEffect, useRef, useState, useCallback } from "react";
import "@innovatrics/dot-document-auto-capture";
import { CameraIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import Head from "../Head";
import Footer from "../../components/Footer";
import { useContext } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import { languageData } from "../../constants/languageData";
import { getDocumentCountry, getDocumentIdType, getDocumentFieldName } from "../../utils/documentFields";
import { residentIDCardName, drivingLicenceName, idCardName, countries } from "../../constants/countries";
import focusIcon from "../../assets/focus.svg";
import DocumentUI from "./DocumentUI";
import DocumentCamera from "./DocumentCamera";
import ImageCheck from "./ImageCheck";
import "@innovatrics/dot-document-auto-capture/events";
import "./docCapture.css";
import uploadFile from "../../assets/upload_file.svg";
import { useParams } from "react-router-dom";
import JourneyConfig from "../../context/JourneyConfig";
import Loader from "../../components/Loader/Loader";
import CameraLoadingOverlay from "../../components/Loader/CameraLoadingOverlay";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import "../../styles/Modal.css";
import { UploadStatusMessages } from "../../constants/failedUpload";
import captureDocButton from "../../assets/captureDocButton.svg";
import MobileCapture from "./MobileCapture";
import GoogleIcon from "../../components/GoogleIcon";

const FrontDocumentCapture = ({
  stepperFunc,
  steps,
  docIteration,
  currentDocumentRetries,
  frontImageRef,
  isFirstDocumentVerificationDone,
  currentDocumentStep,
  currentStep,
  setCapturingMode,
  capturingMode,
  setAutoCaptureLoadingTime,
}) => {
  const instructionChangeEvent = "document-auto-capture:instruction-changed";
  const { config } = useContext(GlobalConfig);
  const { journey, setJourney, getJourney } = useContext(JourneyConfig);
  const [imageUrl, setImageUrl] = useState("");
  const [loadTiming, setLoadTiming] = useState(performance.now());
  const [captureStatus, setCaptureStatus] = useState("document_not_present");
  const [isLoading, setIsLoading] = useState(false);
  const documentFailureReason = sessionStorage.getItem('documentFailureReason');
  const [sameSideDocumentFlagNumber, setSameSideDocumentFlagNumber] = useState(
    documentFailureReason
      ? documentFailureReason === "ONLY_FRONT"
        ? 1
        : documentFailureReason === "ONLY_BACK"
          ? 2
          : -1
      : -1
  );
  const { journeyId } = useParams();
  const isMobile = useDeviceDetection();
  const innovatricsCameraRef = useRef(null);
  const streamRef = useRef(null);
  const language = sessionStorage.getItem("language") ?? "en";
  const { branding } = config ?? {};
  const documentCapture = Array.isArray(config?.documentCapture)
    ? config?.documentCapture[currentDocumentStep]
    : config?.documentCapture;
  const { capturedData: journeyData } = journey ?? {};
  const color = config?.branding?.accentColor;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  const env = process.env.NODE_ENV || "development";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraRunning, setIsCameraRunning] = useState(false);
  const timeoutRef = useRef(null);
  const [message, setMessage] = useState("");
  const videoRef = useRef(null); // Reference to the video element
  const canvasRef = useRef(null); // Reference to the canvas element
  const [facingMode, setFacingMode] = useState(
    isMobile ? "environment" : "user"
  );
  const [width, setWidth] = useState(window.innerWidth);
  const [captureMode, setCaptureMode] = useState("auto");
  const fileInputRef = useRef(null);
  // This document's own country and type, not the first document's (IDX-677).
  const [countryCode] = useState(() => getDocumentCountry(journey, currentDocumentStep));
  const [countryName] = useState(
    countryCode
      ? countries?.find((country) => country.value === countryCode)?.label
      : null
  );
  const [documentName] = useState(() => getDocumentIdType(journey, currentDocumentStep));

  useEffect(() => {
    const cleanupCameras = async () => {
      try {
        sessionStorage.removeItem('isCameraPage');
        const video = videoRef?.current;
        stopSafeStreamFromRef();
        console.log('[FrontCapture][cleanupCameras] stream cleared without error')
        if (video?.srcObject) {
          video.srcObject = null;
        }
      } catch (error) {
        console.log("[FrontCapture][cleanupCameras] Error during clean-up : ",error)
      }
    };
    sessionStorage.setItem('isCameraPage', 'true');
    return () => {
      cleanupCameras().catch(error => {
        console.error('Cleanup error:', error);
      });
    };
  }, []); 
  
  useEffect(() => {
    if (isMobile) {
      setFacingMode("environment");
    }
  }, [isMobile]);

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
  }, [journeyId, journey, getJourney]);

  useEffect(() => {    
    // Set up 5-second fallback
    timeoutRef.current = setTimeout(() => {
      if (!isCameraRunning) {
        console.log('[FrontCapture][CameraReadyState] ⏱️ Fallback triggered - marking camera as ready after timeout');
        setIsCameraRunning(true);
      }
    }, 5000);
  
    const handleStateChanged = (event) => {
      if (!event.detail) {
        console.warn('[FrontCapture][CameraReadyState] Received state change event without details', event);
        return;
      }
      console.log(`[FrontCapture][CameraReadyState] State changed: ${event?.detail?.appState}`, JSON.stringify(event?.detail));
      if (event?.detail?.appState === 'RUNNING') {
        const durationInSeconds = ((performance.now() - loadTiming) / 1000).toFixed(2);
        setAutoCaptureLoadingTime(durationInSeconds);
        console.log('[FrontCapture][CameraReadyState] 🎥 Camera is now operational');
        if(innovatricsCameraRef.current){
          console.log('[FrontCapture][CameraReadyState] current stream set to innov. camera stream');
          streamRef.current = innovatricsCameraRef.current?.getVideoElement();
        }
        setIsCameraRunning(true);
        
        if (timeoutRef.current) {
          console.log('[FrontCapture][CameraReadyState] Clearing fallback timeout');
          clearTimeout(timeoutRef.current);
        }
      }else if (event?.detail?.appState === 'ERROR') {
        console.log('[FrontCapture][CameraReadyState] Error occured during camera initialisation . setting cameraRunning state to true so that other buttons should be enabled');
        setIsCameraRunning(true);
      }
    };
  
    console.log('[FrontCapture][CameraReadyState] Adding event listener...');
    document.addEventListener(
      'document-auto-capture:state-changed', 
      handleStateChanged
    );
  
    return () => {
      console.log('[FrontCapture][CameraReadyState] Cleaning up...');
      if (timeoutRef.current) {
        console.log('[FrontCapture][CameraReadyState] Clearing pending timeout');
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener(
        'document-auto-capture:state-changed',
        handleStateChanged
      );
    };
  }, []);

  const handleFileChange = (e) => {
    try {
      // Log when file selection starts
      console.log('[FrontCapture][File Upload] File selection triggered');
  
      const file = e.target.files[0];
      setCapturingMode("UPLOAD")
      console.log("capture mode here", capturingMode) 
      // Log basic file info
      if (file) {
        console.log('[FrontCapture][File Upload] Selected file:', {
          name: file?.name,
          type: file?.type,
          size: file?.size,
        });
      } else {
        console.warn('[FrontCapture][File Upload] No file selected');
        return;
      }
  
      // India supports PDF uploads, same as other IDs.
      const allowedExtensions = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
  
      // Validate file type
      if (!allowedExtensions.includes(file.type)) {
        const errorMsg = `Invalid file type: ${file.type}. Allowed: ${allowedExtensions.join(', ')}`;
        console.error('[FrontCapture][File Upload]', errorMsg);
        
        setMessage(
          languageData[language]?.failedUpload?.text ||
          UploadStatusMessages.FailedUploadText
        );
        setIsModalOpen(true);
        e.target.value = ''; // Clear the input
        return;
      }
  
      // Validate file size
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        const errorMsg = `File too large: ${sizeInMB}MB. Max allowed: 5MB`;
        console.error('[FrontCapture][File Upload]', errorMsg);
        
        setMessage(
          languageData[language]?.failedUpload?.text ||
          UploadStatusMessages.FailedUploadText
        );
        setIsModalOpen(true);
        e.target.value = ''; // Clear the input
        return;
      }
  
      // Process valid file
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        e.target.value = '';
        console.log('[FrontCapture][File Upload] File successfully converted to base64, length:', base64Data.length);
        setImageUrl(base64Data);
        console.log('[FrontCapture][File Upload] File converted to base64 and processing completed successfully');
      };
      reader.readAsDataURL(file);
  
    } catch (error) {
      console.error('[FrontCapture][File Upload] Unexpected error:', {
        name: error?.name,
        message: error.message,
        stack: error?.stack ,
        additionalInfo: {
          file: error?.fileName || undefined,
          line: error?.lineNumber || undefined,
          column: error?.columnNumber || undefined,
          // Add any other relevant context here
          timestamp: new Date().toISOString(),
          userAgent: navigator?.userAgent
        },
        error : error
      });
    }
  };

  const stopSafeStreamFromRef = useCallback(() => {
    console.log('[FrontCapture][safeStream] Attempting to stop stream from streamRef');
  
    const stream = streamRef?.current;
    if (!stream) {
      console.log('[FrontCapture][safeStream] No stream found in streamRef - skipping');
      return;
    }
  
    try {
      const tracks = stream.getTracks();
      console.log(`[FrontCapture][safeStream] Found ${tracks.length} tracks to stop`);
  
      tracks.forEach((track) => {
        console.log(`[FrontCapture][safeStream] Stopping ${track?.kind} track (ID: ${track?.id})`);
        track.stop();
        console.log(`[FrontCapture][safeStream] Track stopped: ${track?.readyState}`);
      });
  
      streamRef.current = null;
      console.log('[FrontCapture][safeStream] Stream cleanup complete and ref cleared');
    } catch (e) {
      console.error('[FrontCapture][safeStream] Stream cleanup failed:', e);
    }
  }, []);    
  
  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleDocumentPhotoTaken = async ({ image, data }, content) => {
    setCapturingMode("AUTO")
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result;
      console.log('[FrontCapture][Camera] Image successfully converted to base64, length:', base64Data.length);
      setImageUrl(base64Data);
    };
    reader.readAsDataURL(image);
  };


  const handleBackButtonClick = () => {
    setJourney({
      ...journey,
      capturedData: { ...journey?.capturedData, [getDocumentFieldName("idType", currentDocumentStep)]: "" },
    });
    stepperFunc(steps[2]);
  };

  useEffect(() => {
    if (isMobile) return;

    const handleInstructionChange = (event) => {
      setCaptureStatus(event.detail.instructionCode);
    };

    document.addEventListener(instructionChangeEvent, handleInstructionChange);

    return () => {
      document.removeEventListener(
        instructionChangeEvent,
        handleInstructionChange
      );
    };
  }, []);

  useEffect(() => {
    if (imageUrl) {
      frontImageRef.current=imageUrl;
      console.log('[FrontCapture] frontImage ref set, navigating to ImageCheck');
      stepperFunc(steps[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    async function startCamera() {
      try {
        stopSafeStreamFromRef();
        const videoConstraints = {
          facingMode: facingMode,
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          zoom: false,
        });
        console.log('[FrontCapture][startCamera] current stream set to manual camera stream');
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream; // Set the video stream to the video element
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
      }
    }

    if (captureMode === "manual") startCamera();
  }, [facingMode, captureMode]); // Only trigger the camera start when facingMode changes

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    setCapturingMode("MANUAL");
    // Flip the context horizontally to mirror the video
    // context.translate(canvas.width, 0);
    // context.scale(-1, 1);

    // Draw the current video frame onto the canvas (flipped)
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert the canvas content to a data URL (image) and store it

    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        console.log('[FrontCapture][Manual Capture] Blob successfully converted to base64, length:', base64Data.length);
        setImageUrl(base64Data);
      };
      reader.readAsDataURL(blob);
    }, "image/png");
  };

  // const navigateToImageCheck = () => {
  //   stepperFunc(steps[0]);
  // };
  const flipCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  };

  if (!imageUrl && isLoading) return <Loader />;

  return (
    <>
      <>
        <Head
          countryCode={countryCode}
          showCountry
          showBackIcon={(journey?.userInfo?.id?.[getDocumentFieldName("country", currentDocumentStep)] && journey?.userInfo?.id?.[getDocumentFieldName("idType", currentDocumentStep)]) ? false : true}
          showLanguages={false}
          handleBack={handleBackButtonClick}
          stepperFunc={stepperFunc}
          showTooltipOption={currentDocumentRetries != 0 ? true : false}
          showMobileText
          style={{ zIndex: "100", position: "relative", top: 0 }}
        />
        <div className="parent-container" style={{ paddingTop: 0 }}>
          <div className="main">
            <div
              className="left-section"
              style={{ flexDirection: "column", background: "black" }}
            >
              <CameraLoadingOverlay isVisible={!isCameraRunning} />
              <>
                {captureMode === "manual" ? (
                  <div
                    style={{ position: "relative", marginBottom: 40 }}
                    className="auto-capture"
                  >
                    <div
                      className="video-container"
                      style={{
                        width: "100%",
                        height:
                          width <= 767
                            ? "100%"
                            : "auto" /* Set container height as desired */,
                        overflow: "hidden",
                        marginBottom: 20,
                      }}
                    >
                      {/* {isMobile && (
                        <button
                          className="flip"
                          onClick={()=>flipCamera()}
                          style={{ marginTop: "10px"}}
                        >
                          <span
                            class="material-symbols-outlined"
                            style={{ color: "white" }}
                          >
                            flip_camera_ios
                          </span>
                        </button>
                      )} */}
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "cover",
                          transform:
                            facingMode === "environment"
                              ? "none"
                              : "scaleX(-1)",
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: "none" }} />
                    </div>

                    <div
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          borderColor: "white",
                          borderWidth: 2,
                          height: 200,
                          width: 300,
                          marginBottom: "20px",
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{ position: "relative" }}
                    className="auto-capture"
                  >
                    {facingMode && (
                      <div
                        className="video-container"
                        style={{
                          width: "100%",
                          height:
                            width <= 767
                              ? "100%"
                              : "auto" /* Set container height as desired */,
                          overflow: "hidden",
                        }}
                      >
                        <DocumentCamera
                          ref={innovatricsCameraRef}
                          cameraFacing={facingMode}
                          onPhotoTaken={handleDocumentPhotoTaken}
                          placeholder="id-solid"
                          backdropColor="none"
                        />
                        <DocumentUI
                          theme={{
                            colors: {
                              placeholderColor: "white",
                              instructionColorSuccess: "#00FF83",
                            },
                          }}
                          placeholder="id-solid"
                          backdropColor="rgba(10, 10, 10, 0.5)"
                          instructions={{}}
                          appStateInstructions={{
                          loading: {
                            text: "",
                            visible: false,
                          },
                        }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>

              {width > 767 &&
                (captureMode === "auto" ? (
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: "center",
                      position: "absolute",
                      bottom: "40px",
                    }}
                  >
                    <div style={{ color: "#919191" }}>
                      {languageData[language]?.documentCapture?.stillNotWorking ??
                        languageData["en"]?.documentCapture?.stillNotWorking}
                    </div>
                    <div
                      style={{
                        textDecorationLine: "underline",
                        color: "white",
                        cursor: "pointer",
                      }}
                      onClick={() => setCaptureMode("manual")}
                    >
                      {languageData[language]?.documentCapture?.clickToCaptureInstead ??
                        languageData["en"]?.documentCapture?.clickToCaptureInstead}
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => captureImage()}
                    style={{
                      position: "absolute",
                      bottom: 24,
                      cursor: "pointer",
                    }}
                  >
                    <img src={captureDocButton} alt="button_link" />
                  </div>
                ))}
            </div>
            <div
              className="right-section md:items-start items-center"
              style={{
                // background: width <= 767 ? "#000000" : "white",
                // color: width > 767 ? "#000000" : "white",
                paddingTop: width <= 767 ? "18px" : "32px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {width > 767 ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div className="flex flex-col md:mt-[80px]">
                      <div className="flex justify-center md:justify-start">
                        <svg
                          width="44"
                          height="44"
                          viewBox="0 0 44 44"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="22"
                            cy="22"
                            r="22"
                            fill={bgColor}
                            style={{ background: bgColor }}
                          />
                          <g clip-path="url(#clip0_1556_28470)">
                            <path
                              d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
                              fill="#1658aa"
                              style={{ fill: color }}
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_1556_28470">
                              <rect
                                width="24"
                                height="24"
                                fill="white"
                                transform="translate(10 10.5)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                      <div
                        className="mt-6 md:mt-4 text-[18px] md:text-[26px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0"
                        style={{ color: color }}
                      >
                        {(sameSideDocumentFlagNumber === 1
                          ? languageData[language]?.backDocCapture?.heading
                          : languageData[language]?.frontDocCapture?.heading) +
                          " " +
                          (documentName == "Resident Id Card"
                            ? residentIDCardName?.[countryCode]
                              ? residentIDCardName?.[countryCode]
                              : "Identity Card"
                            : documentName == "Passport"
                              ? `${countryName} Passport`
                              : documentName == "Driving License"
                              ? drivingLicenceName?.[countryCode]
                                ? drivingLicenceName?.[countryCode]
                                : documentName
                              : documentName == "Id Card"
                              ? idCardName?.[countryCode]
                                ? idCardName?.[countryCode]
                                : documentName
                              : documentName) +
                          " " +
                          (languageData[language]?.frontDocCapture?.headingBack
                            ? languageData[language]?.frontDocCapture
                              ?.headingBack
                            : languageData["en"]?.frontDocCapture?.headingBack)}
                      </div>
                    </div>
                  </div>
                  {documentCapture?.uploadAllowed && (
                    <div className="flex flex-col md:justify-end items-center md:gap-[12px] w-full md:mt-24">
                      <div
                        style={{
                          color: "#919191",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {languageData[language]?.documentCapture?.fileFormats ??
                          languageData["en"]?.documentCapture?.fileFormats}
                      </div>
                      <button
                        className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium mt-3 md:mt-[0px]"
                        style={{
                          borderColor: color,
                          borderWidth: 1,
                          color: color,
                        }}
                        onClick={() => fileInputRef.current.click()}
                      >
                        {languageData[language]?.frontDocCapture?.uploadButton ??
                          languageData["en"]?.frontDocCapture?.uploadButton}{" "}
                        <GoogleIcon
                          iconName="upload"
                          size={18}
                          style={{ color: color }}
                        />
                      </button>
                    </div>
                  )}
                  <Footer />
                </div>
              ) : (
                <MobileCapture
                  currentDocumentStep={currentDocumentStep}
                  uploadAllowed={documentCapture?.uploadAllowed}
                  color={color}
                  sameSideDocumentFlagNumber={sameSideDocumentFlagNumber}
                  journey={journey}
                  docType="front"
                  captureMode={captureMode}
                  switchToManual={() => {
                    setCaptureMode("manual");
                  }}
                  captureClick={() => {
                    captureImage();
                  }}
                  uploadClick={() => {
                    try {
                      if (!fileInputRef.current) {
                        console.error("[FrontCapture][uploadClick] File input ref is null!");
                        return;
                      }
                      console.log("[FrontCapture][uploadClick]Triggering file input...");
                      fileInputRef.current.click();
                    } catch (error) {
                      console.error("[FrontCapture][uploadClick] Failed to open file picker(Format-1):", (JSON.stringify(error)));
                      console.error("[FrontCapture][uploadClick] Failed to open file picker(Format-2):",JSON.stringify({
                        name: error?.name,
                        message: error?.message,
                        stack: error?.stack
                      }));
                    
                    }
                  }}          
                  skipClick={() => { }}
                />
              )}
            </div>
          </div>
        </div>
      </>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <GoogleIcon
                iconName="cancel"
                size={40}
                style={{ color: "#F14437" }}
                className="mb-4"
              />
            </div>
            <div>
              {languageData[language]?.failedUpload?.title ||
                UploadStatusMessages.FailedUploadTitle}
            </div>
            <p className="mt-4">{message}</p>
            <button
              className="px-8 py-4 mt-12 md:mt-20"
              style={{
                border: "1px solid rgba(0, 0, 0, 0.10)",
                position: "relative",
                borderRadius: 4,
                width: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                background: color,
                color: "white",
              }}
              onClick={handleClose}
            >
              {languageData[language]?.failedUpload?.btn ||
                UploadStatusMessages.FailedUploadButton}
            </button>
          </div>
        </div>
      )}

      {/* upload file */}
      <div style={{ 
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
      }}>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg, .png, .jpeg, .pdf"
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

export default FrontDocumentCapture;
